// frontend/src/components/inbox/inboxShape.ts
/**
 * SA-1092 §2: the one rule that shapes the Inbox's bar and thread header per
 * deployment, the `navModel.buildNav` precedent — pure, tested per profile.
 *
 * A TRIAGE DESK = email ingestion with at least one router mailbox. A CHAT
 * CONSOLE = a chat SOURCE: a persona with a WhatsApp config (the New
 * Conversation dialog's list) or a chat thread in the Inbox (the stats'
 * channel breakdown, whatsapp, web or widget); never a flag alone — compass-staging
 * runs knowledge-ops, which carries `widget` and `whatsapp` for groups. Both
 * = the union, by channel chip: the console's controls show under All,
 * WhatsApp and Web and go under Email and WhatsApp group. Neither = today's
 * bar. Not loaded = the tabs and the search only, so no control appears and
 * vanishes. A deployment with the `email_ingestion` flag and no router
 * mailbox (Jaso on smb) keeps the Email chip and the Triage tab the flag
 * alone always showed (R2: its bar byte for byte); the tab lists its queue,
 * which is empty. New Conversation starts a WhatsApp chat, so it shows only
 * with a WhatsApp persona (SA-1380): a console of widget chats alone has none.
 */
import type { InboxViewTab } from '@/types/inbox'
import { defaultSort, sortValues } from './sortText'

export interface InboxShapeInput {
  features: (flag: string) => boolean
  routerMailboxes: number
  chatPersonas: number
  /** Conversations on a chat channel (whatsapp, web, widget) the Inbox holds. */
  chatThreads: number
  loaded: boolean
}

export type ShapeKind = 'desk' | 'console' | 'union' | 'none' | 'loading'

export interface InboxShape {
  kind: ShapeKind
  desk: boolean
  console: boolean
  tabs: InboxViewTab[]
  headCount: 'waiting' | 'console' | 'both' | 'none'
  channels: string[]
  triageChips: boolean
  statusRow: boolean
  needsReview: boolean
  faults: boolean
  newConversation: boolean
  groupBySender: boolean
  searchPlaceholder: string
  sortSelect: boolean
}

export const CONSOLE_CHANNELS = ['whatsapp', 'web'] as const
export const DESK_CHANNELS = ['email', 'whatsapp-group'] as const
/** SA-1370: the channels a persona answers — a WhatsApp, web or widget chat;
 * never a router channel. Not the chips: those are `CONSOLE_CHANNELS`. */
export const AI_CHANNELS = ['whatsapp', 'web', 'widget'] as const

export const WAITING_COUNT_LABEL = (n: number) => `${n} waiting for a decision`

/** SA-1380: the chat threads the Inbox holds, from the stats' channel
 * breakdown — a thread on a channel a persona answers (`AI_CHANNELS`), the
 * widget's included; 0 until the stats arrive. */
export function chatThreadCount(breakdown: Record<string, number> | undefined): number {
  return AI_CHANNELS.reduce((sum, channel) => sum + (breakdown?.[channel] ?? 0), 0)
}

const BROWSE_TABS: InboxViewTab[] = ['open', 'resolved', 'archived']
const DESK_TABS: InboxViewTab[] = ['open', 'triage', 'resolved', 'archived']

export function inboxShape(
  input: InboxShapeInput,
  context: { viewTab: InboxViewTab; channelFilter: string | null },
): InboxShape {
  if (!input.loaded) {
    return {
      kind: 'loading', desk: false, console: false, tabs: BROWSE_TABS, headCount: 'none', channels: [],
      triageChips: false, statusRow: false, needsReview: false, faults: false, newConversation: false,
      groupBySender: false, searchPlaceholder: 'Search...', sortSelect: false,
    }
  }
  const desk = input.features('email_ingestion') && input.routerMailboxes > 0
  const console = input.chatPersonas > 0 || input.chatThreads > 0
  const kind: ShapeKind = desk && console ? 'union' : desk ? 'desk' : console ? 'console' : 'none'
  // Today's gates on the flag alone (R2): the Email chip and the Triage tab.
  const legacyEmail = !desk && input.features('email_ingestion')
  const consoleControls = consoleControlsUnder(kind, context.channelFilter)
  // SA-1104: the queue ignores Faults and Needs review, so the Triage tab
  // hides them, as it hides the sort select; their selections are kept.
  const onTriage = context.viewTab === 'triage'
  const channels: string[] = ['all']
  if (kind === 'none' || kind === 'console' || kind === 'union') channels.push(...CONSOLE_CHANNELS)
  if (desk) {
    channels.push('email')
    if (input.features('whatsapp_groups')) channels.push('whatsapp-group')
  } else if (legacyEmail) {
    channels.push('email')
  }
  return {
    kind, desk, console,
    tabs: desk || legacyEmail ? DESK_TABS : BROWSE_TABS,
    headCount: desk && console ? 'both' : desk ? 'waiting' : 'console',
    channels,
    triageChips: desk,
    statusRow: consoleControls,
    needsReview: consoleControls && !onTriage && input.features('commerce'),
    faults: consoleControls && !onTriage,
    newConversation: (kind === 'console' || kind === 'union') && input.chatPersonas > 0,
    groupBySender: consoleControls || (kind === 'union' && context.channelFilter === 'whatsapp-group'),
    searchPlaceholder: kind === 'desk' ? 'Search mail...' : kind === 'union' ? 'Search...' : 'Search customers...',
    sortSelect: context.viewTab !== 'triage',
  }
}

/** On the union the console's controls follow the channel chip: they show
 * under All, WhatsApp and Web and go under Email and WhatsApp group. */
function consoleControlsUnder(kind: ShapeKind, channelFilter: string | null): boolean {
  const underDeskChip = (DESK_CHANNELS as readonly string[]).includes(channelFilter ?? '')
  return kind === 'none' || kind === 'console' || (kind === 'union' && !underDeskChip)
}

/** SA-1104: what a channel chip change writes beside the chip, so the store
 * takes ONE write and the list one fetch: the console's selections dropped
 * when the new chip hides its controls, and the new chip's default sort when
 * its option set differs (`sortValues`). The loading shape writes nothing. */
export function chipWrites(
  kind: ShapeKind, from: string | null, to: string | null,
): { clearConsole: boolean; sortBy: string | null } {
  if (kind === 'loading') return { clearConsole: false, sortBy: null }
  return {
    clearConsole: !consoleControlsUnder(kind, to),
    sortBy: sortValues(from, kind) === sortValues(to, kind) ? null : defaultSort(to, kind),
  }
}

/** §2.3: a thread has an AI when it is a direct chat, a widget chat included
 * (SA-1370); never on a triaged channel (a router thread has nothing to take
 * over from). */
export function threadHasAi(thread: { channel: string }): boolean {
  return (AI_CHANNELS as readonly string[]).includes(thread.channel)
}
